import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown, ChevronLeft, ChevronRight as ChevronOpen,
  Layers, ShieldCheck, Users, Building2, FileText,
  Lock, Briefcase, ClipboardList, Warehouse, BarChart,
  Settings2, FolderSearch, Mail, MapPin, Map
} from "lucide-react";
import { FiLayers } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import axios from "../services/api";
import { slugify } from "../utils/slugify";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";

// Helpers
const deslugify = (slug) =>
  slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const adminSections = {
  Admin: ["Users", "Companies", "Reports", "Authorized Reports", "Departments", "Demo Requests"],
};

const adminIcons = {
  Users,
  Companies: Building2,
  Reports: FileText,
  "Authorized Reports": Lock,
  Departments: FiLayers,
  "Demo Requests": Mail,
};

const homzreadySections = [
  "HomzReady Clients",
  "Service Providers",
  "Property Types",
  "Locations",
  "Service Types",
  "Areas",
  "Projects",
  "ProjectsManager",
];

const homzreadyIcons = {
  "HomzReady Clients": Users,
  "Service Providers": Building2,
  "Property Types": Layers,
  Locations: MapPin,
  "Service Types": ClipboardList,
  Areas: Map,
  Projects: FolderSearch,
};

const departmentIcons = {
  Accounting: BarChart,
  Procurement: ClipboardList,
  Inventory: Warehouse,
  "Project Managment": Briefcase,
  Tools: Settings2,
  default: FolderSearch,
};

const sidebarVariants = {
  open: { width: 288 },
  collapsed: { width: 72 },
};

const itemVariants = {
  hidden: { opacity: 0, height: 0, y: -10 },
  visible: { opacity: 1, height: "auto", y: 0 },
  exit: { opacity: 0, height: 0, y: -10 },
};

export default function Sidebar({ collapsed, onToggleCollapse }) {
  const [openSections, setOpenSections] = useState({});
  const [groupedReports, setGroupedReports] = useState({});
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
  const { t } = useTranslation();
  const hasFetchedReports = useRef(false);

  // GA
  const handleWhatsClick = () => {
    ReactGA.event({ category: "Button", action: "Click", label: "WhatsApp Clicked" });
  };

  // Determine if user's company indicates "HomzReady"
  const isHomzReadyCompany = (() => {
    const candidatesRaw = [
      user?.company,
      user?.company_id,
      user?.companyId,
      user?.company_name,
      user?.company?.name,
      user?.company_id?.name,
      user?.companyId?.name,
    ].filter(Boolean);

    const candidates = candidatesRaw
      .map((v) =>
        typeof v === "string"
          ? v.trim().toLowerCase()
          : typeof v?.name === "string"
          ? v.name.trim().toLowerCase()
          : ""
      )
      .filter(Boolean);

    return candidates.some((v) => v === "homzready");
  })();

  useEffect(() => {
    const fetchAuthorizedReports = async () => {
      try {
        const res = await axios.get("/authorized-reports/user");
        if (res.data && typeof res.data === "object") {
          setGroupedReports(res.data);
          hasFetchedReports.current = true;
        } else {
          console.warn("⚠️ Invalid grouped report format", res.data);
        }
      } catch (error) {
        console.error("❌ Failed to fetch authorized reports", error);
      }
    };

    if (user && !hasFetchedReports.current) {
      fetchAuthorizedReports();
    }
  }, [user]);

  useEffect(() => {
    Object.entries(groupedReports).forEach(([dept, reports]) => {
      reports.forEach((r) => {
        const reportPath = `/reports/${slugify(dept)}/${slugify(r.title)}`;
        if (location.pathname === reportPath) {
          setOpenSections((prev) => ({ ...prev, [dept]: true }));
        }
      });
    });
  }, [location.pathname, groupedReports]);

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLinkClick = () => {
    if (isMobile && !collapsed) onToggleCollapse();
  };

  const whatsappMessage = encodeURIComponent(t("whatsappAutoMessage"));
  const whatsappLink = `https://wa.me/966582076578?text=${whatsappMessage}`;

  return (
    <motion.aside
      initial={collapsed ? "collapsed" : "open"}
      animate={collapsed ? "collapsed" : "open"}
      variants={sidebarVariants}
      transition={{ type: "spring", stiffness: 260, damping: 25 }}
      className="bg-gradient-to-br from-gray-900 to-gray-800 text-white shadow-xl fixed h-full z-50 flex flex-col"
    >
      {/* Header */}
      <div className="relative py-6 px-4 flex items-center justify-between">
        <Layers className="text-blue-400" size={28} />
        {!collapsed && (
          <h2 className="absolute left-1/2 transform -translate-x-1/2 text-2xl font-bold">Reports</h2>
        )}
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={onToggleCollapse}
          className="bg-blue-500 hover:bg-blue-600 rounded-full p-1 shadow-lg transition duration-200"
        >
          {collapsed ? <ChevronOpen size={20} /> : <ChevronLeft size={20} />}
        </motion.button>
      </div>

      {/* Navigation */}
      <nav className="flex-grow px-2 space-y-2 overflow-y-auto">
        {Object.entries(groupedReports).map(([dept, reports]) => {
          const DepartmentIcon = departmentIcons[dept] || departmentIcons.default;
          const slugDept = slugify(dept);

          return (
            <div key={dept}>
              <motion.button
                whileTap={{ scale: 0.98 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => toggleSection(dept)}
                className={`w-full flex items-center justify-between py-2 px-2 rounded-lg ${
                  openSections[dept] ? "bg-gray-700" : "hover:bg-gray-700"
                } transition-all duration-200`}
              >
                <span className="flex items-center">
                  <DepartmentIcon
                    size={20}
                    className="text-blue-400 hover:animate-pulse"
                    onClick={onToggleCollapse}
                  />
                  {!collapsed && <span className="ml-3 font-semibold tracking-wide">{dept}</span>}
                </span>
                {!collapsed && (
                  <motion.span
                    animate={{ rotate: openSections[dept] ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown size={20} />
                  </motion.span>
                )}
              </motion.button>

              <AnimatePresence>
                {openSections[dept] && !collapsed && (
                  <motion.ul
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={itemVariants}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="mt-2 ml-10 border-l-2 border-blue-500 pl-4"
                  >
                    {reports.length === 0 ? (
                      <li className="text-sm italic text-gray-400 py-2">No Reports Available</li>
                    ) : (
                      reports.map((r) => {
                        const reportSlug = slugify(r.title);
                        const reportPath = `/reports/${slugDept}/${reportSlug}`;
                        const isActive = location.pathname === reportPath;

                        return (
                          <motion.li key={r.id} whileHover={{ x: 6 }} className="mb-2">
                            <Link
                              to={reportPath}
                              onClick={handleLinkClick}
                              className={`block py-2 px-3 rounded-md font-medium transition duration-200 ${
                                isActive
                                  ? "bg-blue-500 text-white shadow-md"
                                  : "hover:bg-gray-600 hover:text-yellow-300"
                              }`}
                            >
                              {r.title}
                            </Link>
                          </motion.li>
                        );
                      })
                    )}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {/* Admin Control — ONLY admin */}
        {user?.role === "admin" && (
          <>
            <hr className="my-4 border-gray-600" />
            <div>
              <motion.button
                whileTap={{ scale: 0.98 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => toggleSection("Admin")}
                className={`w-full flex items-center justify-between py-2 px-2 rounded-lg ${
                  openSections["Admin"] ? "bg-gray-700" : "hover:bg-gray-700"
                } transition-all duration-200`}
              >
                <span className="flex items-center">
                  <ShieldCheck className="text-yellow-400 hover:animate-pulse" size={20} />
                  {!collapsed && <span className="ml-3 font-semibold tracking-wide">Admin Control</span>}
                </span>
                {!collapsed && (
                  <motion.span
                    animate={{ rotate: openSections["Admin"] ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown size={20} />
                  </motion.span>
                )}
              </motion.button>

              <AnimatePresence>
                {openSections["Admin"] && !collapsed && (
                  <motion.ul
                    key="Admin"
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={itemVariants}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="mt-2 ml-10 border-l-2 border-yellow-400 pl-4"
                  >
                    {adminSections["Admin"].map((item) => {
                      const AdminIcon = adminIcons[item];
                      const path = `/admin/${slugify(item)}`;
                      const isActive = location.pathname === path;

                      return (
                        <motion.li key={item} whileHover={{ x: 6 }} className="mb-2">
                          <Link
                            to={path}
                            onClick={handleLinkClick}
                            className={`flex items-center py-2 px-3 rounded-md font-medium transition duration-200 ${
                              isActive
                                ? "bg-yellow-500 text-white shadow-md"
                                : "hover:bg-gray-600 hover:text-yellow-300"
                            }`}
                          >
                            <AdminIcon size={18} className="mr-2 hover:animate-pulse transition duration-300" />
                            {item}
                          </Link>
                        </motion.li>
                      );
                    })}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          </>
        )}

        {/* HomzReady Admin — admin OR HomzReady company */}
        {(user?.role === "admin" || isHomzReadyCompany) && (
          <>
            <hr className="my-4 border-gray-600" />
            <div>
              <motion.button
                whileTap={{ scale: 0.98 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => toggleSection("HomzReady")}
                className={`w-full flex items-center justify-between py-2 px-2 rounded-lg ${
                  openSections["HomzReady"] ? "bg-gray-700" : "hover:bg-gray-700"
                } transition-all duration-200`}
              >
                <span className="flex items-center">
                  <Briefcase className="text-green-400 hover:animate-pulse" size={20} />
                  {!collapsed && <span className="ml-3 font-semibold tracking-wide">HomzReady Admin</span>}
                </span>
                {!collapsed && (
                  <motion.span
                    animate={{ rotate: openSections["HomzReady"] ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown size={20} />
                  </motion.span>
                )}
              </motion.button>

              <AnimatePresence>
                {openSections["HomzReady"] && !collapsed && (
                  <motion.ul
                    key="HomzReady"
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={itemVariants}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="mt-2 ml-10 border-l-2 border-green-400 pl-4"
                  >
                    {homzreadySections.map((item) => {
                      const Icon = homzreadyIcons[item] || FolderSearch;
                      const path = `/homzready/${slugify(item)}`;
                      const isActive = location.pathname === path;

                      return (
                        <motion.li key={item} whileHover={{ x: 6 }} className="mb-2">
                          <Link
                            to={path}
                            onClick={handleLinkClick}
                            className={`flex items-center py-2 px-3 rounded-md font-medium transition duration-200 ${
                              isActive
                                ? "bg-green-500 text-white shadow-md"
                                : "hover:bg-gray-600 hover:text-green-300"
                            }`}
                          >
                            <Icon size={18} className="mr-2 transition duration-300" />
                            {item}
                          </Link>
                        </motion.li>
                      );
                    })}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          </>
        )}
      </nav>

      {/* Footer Contact Section */}
      <div className="mt-auto p-4 bg-gray-950 text-base border-t border-gray-700">
        {!collapsed ? (
          <div className="space-y-3 text-sm font-medium">
            <Link
              to="/ContactUsPage"
              onClick={handleLinkClick}
              className="block text-blue-400 hover:text-white transition"
            >
              📬 Contact Us
            </Link>
            <a
              href="https://wa.me/966582076578?text=Hello%2C%20I%20need%20assistance%20with%20InsightsFlows."
              target="_blank"
              rel="noopener noreferrer"
              className="block text-green-400 hover:text-white transition"
              onClick={handleWhatsClick}
            >
              💬 Chat with Us
            </a>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2 text-xl">
            <Link
              to="/ContactUsPage"
              onClick={handleLinkClick}
              title="Contact Us"
              className="text-blue-400 hover:text-white"
            >
              📬
            </Link>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              title="Chat with Us"
              className="text-green-400 hover:text-white"
              onClick={handleWhatsClick}
            >
              💬
            </a>
          </div>
        )}
      </div>
    </motion.aside>
  );
}
